import { useState, useEffect } from "react";
import axios from "axios";
import API_ENDPOINTS from "config/api";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import PropTypes from "prop-types";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";

export default function RutasDespachos() {
  const [rutas, setRutas] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editando, setEditando] = useState(false);
  const [rutaActual, setRutaActual] = useState({
    codigo: "",
    nombre: "",
    conductor_id: "",
    vehiculo_id: "",
    zona_id: "",
    fecha_inicio: "",
    hora_inicio: "",
    fecha_fin: "",
    hora_fin: "",
    distancia_km: "",
    tiempo_estimado: "",
    paradas: "",
    estado: "planificada",
    observaciones: "",
  });

  const [conductoresDisponibles, setConductoresDisponibles] = useState([]);
  const [vehiculosDisponibles, setVehiculosDisponibles] = useState([]);
  const [zonasDisponibles, setZonasDisponibles] = useState([]);

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      const [rutasRes, conductoresRes, vehiculosRes, zonasRes] = await Promise.all([
        axios.get(API_ENDPOINTS.LOGISTICA.RUTAS),
        axios.get(API_ENDPOINTS.LOGISTICA.CONDUCTORES),
        axios.get(API_ENDPOINTS.LOGISTICA.FLOTAS),
        axios.get(API_ENDPOINTS.LOGISTICA.ZONAS || "http://localhost:8000/api/logistica/zonas"), // Fallback if config matches not updated
      ]);
      setRutas(rutasRes.data);
      setConductoresDisponibles(conductoresRes.data);
      setVehiculosDisponibles(vehiculosRes.data);
      setZonasDisponibles(zonasRes.data);
    } catch (error) {
      console.error("Error al cargar datos de rutas:", error);
    }
  };

  useEffect(() => {
    cargarDatos();
  }, []);

  const handleOpenDialog = (ruta = null) => {
    if (ruta) {
      setEditando(true);
      setRutaActual({
        ...ruta,
        // For backend data mapping, ensure IDs match names or use objects if provided by backend
        // This logic assumes table display sends names but Dialog needs IDs.
        // If backend sends Objects in Rutas list, check accessors.
        conductor_id:
          conductoresDisponibles.find(
            (c) => c.nombre === (ruta.conductor?.nombre || ruta.conductor)
          )?.id || "",
        vehiculo_id:
          vehiculosDisponibles.find((v) => v.placa === (ruta.vehiculo?.placa || ruta.vehiculo))
            ?.id || "",
        zona_id:
          zonasDisponibles.find((z) => z.nombre === (ruta.zona?.nombre || ruta.zona))?.id || "",
        fecha_inicio: ruta.fecha_inicio || ruta.fecha, // map backend field safely
        distancia_km: ruta.distancia_km || ruta.distancia,
        tiempo_estimado: calcularTiempoEstimado(ruta.hora_inicio, ruta.hora_fin),
      });
    } else {
      setEditando(false);
      setRutaActual({
        codigo: "",
        nombre: "",
        conductor_id: "",
        vehiculo_id: "",
        zona_id: "",
        fecha_inicio: new Date().toISOString().split("T")[0],
        hora_inicio: "",
        fecha_fin: new Date().toISOString().split("T")[0],
        hora_fin: "",
        distancia_km: "",
        tiempo_estimado: "",
        paradas: "",
        estado: "planificada",
        observaciones: "",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditando(false);
  };

  const calcularTiempoEstimado = (inicio, fin) => {
    if (!inicio || !fin) return "";
    const [h1, m1] = inicio.split(":").map(Number);
    const [h2, m2] = fin.split(":").map(Number);
    const minutos = h2 * 60 + m2 - (h1 * 60 + m1);
    const horas = Math.floor(minutos / 60);
    const mins = minutos % 60;
    return `${horas}h ${mins}m`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRutaActual({ ...rutaActual, [name]: value });
    // Calcular tiempo estimado automáticamente
    if (name === "hora_inicio" || name === "hora_fin") {
      const inicio = name === "hora_inicio" ? value : rutaActual.hora_inicio;
      const fin = name === "hora_fin" ? value : rutaActual.hora_fin;
      if (inicio && fin) {
        setRutaActual((prev) => ({
          ...prev,
          [name]: value,
          tiempo_estimado: calcularTiempoEstimado(inicio, fin),
        }));
      }
    }
  };

  const handleGuardar = async () => {
    try {
      if (editando) {
        await axios.put(`${API_ENDPOINTS.LOGISTICA.RUTAS}/${rutaActual.id}`, rutaActual);
      } else {
        await axios.post(API_ENDPOINTS.LOGISTICA.RUTAS, rutaActual);
      }
      handleCloseDialog();
      handleCloseDialog();
      cargarDatos();
    } catch (error) {
      console.error("Error al guardar ruta:", error);
    }
  };

  const handleIniciarRuta = (id) => {
    // Llamada API para iniciar ruta
    console.log("Iniciando ruta:", id);
    cargarDatos();
  };

  const handleCompletarRuta = (id) => {
    // Llamada API para completar ruta
    console.log("Completando ruta:", id);
    cargarDatos();
  };

  const handleEliminar = async (id) => {
    if (window.confirm("¿Está seguro de eliminar esta ruta?")) {
      try {
        await axios.delete(`${API_ENDPOINTS.LOGISTICA.RUTAS}/${id}`);
        cargarDatos();
      } catch (error) {
        console.error("Error al eliminar ruta:", error);
      }
    }
  };

  const getEstadoChip = (estado) => {
    const estados = {
      planificada: { color: "info", label: "Planificada", icon: "event" },
      en_curso: { color: "warning", label: "En Curso", icon: "directions_car" },
      completada: { color: "success", label: "Completada", icon: "check_circle" },
      cancelada: { color: "error", label: "Cancelada", icon: "cancel" },
    };
    const est = estados[estado] || estados.planificada;
    return (
      <Chip
        icon={<Icon fontSize="small">{est.icon}</Icon>}
        label={est.label}
        color={est.color}
        size="small"
      />
    );
  };

  /* eslint-disable react/prop-types */
  const columns = [
    { Header: "Código", accessor: "codigo", width: "10%" },
    { Header: "Nombre", accessor: "nombre", width: "15%" },
    { Header: "Conductor", accessor: "conductor", width: "12%" },
    { Header: "Vehículo", accessor: "vehiculo", width: "10%" },
    { Header: "Zona", accessor: "zona", width: "10%" },
    { Header: "Fecha", accessor: "fecha", width: "10%" },
    { Header: "Horario", accessor: "horario", width: "10%" },
    {
      Header: "Progreso",
      accessor: "progreso",
      width: "12%",
      Cell: ({ value, row }) => (
        <MDBox>
          <MDTypography variant="caption" fontWeight="medium">
            {row.original.entregas_completadas}/{row.original.paradas} entregas
          </MDTypography>
          <MDBox
            sx={{
              width: "100%",
              height: 6,
              bgcolor: "grey.300",
              borderRadius: 1,
              mt: 0.5,
            }}
          >
            <MDBox
              sx={{
                width: `${value}%`,
                height: "100%",
                bgcolor: value === 100 ? "success.main" : value > 0 ? "warning.main" : "info.main",
                borderRadius: 1,
              }}
            />
          </MDBox>
        </MDBox>
      ),
    },
    {
      Header: "Estado",
      accessor: "estado",
      width: "8%",
      Cell: ({ value }) => getEstadoChip(value),
    },
    {
      Header: "Acciones",
      accessor: "acciones",
      width: "8%",
      Cell: ({ row }) => (
        <MDBox display="flex" gap={1}>
          {row.original.estado === "planificada" && (
            <Tooltip title="Iniciar Ruta">
              <IconButton
                size="small"
                color="success"
                onClick={() => handleIniciarRuta(row.original.id)}
              >
                <Icon fontSize="small">play_arrow</Icon>
              </IconButton>
            </Tooltip>
          )}
          {row.original.estado === "en_curso" && (
            <Tooltip title="Completar Ruta">
              <IconButton
                size="small"
                color="info"
                onClick={() => handleCompletarRuta(row.original.id)}
              >
                <Icon fontSize="small">check</Icon>
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Editar">
            <IconButton size="small" color="info" onClick={() => handleOpenDialog(row.original)}>
              <Icon fontSize="small">edit</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title="Ver Detalles">
            <IconButton size="small" color="primary">
              <Icon fontSize="small">visibility</Icon>
            </IconButton>
          </Tooltip>
          {row.original.estado === "planificada" && (
            <Tooltip title="Eliminar">
              <IconButton
                size="small"
                color="error"
                onClick={() => handleEliminar(row.original.id)}
              >
                <Icon fontSize="small">delete</Icon>
              </IconButton>
            </Tooltip>
          )}
        </MDBox>
      ),
    },
  ];
  /* eslint-enable react/prop-types */

  const rows = rutas.map((ruta) => ({
    ...ruta,
    horario: `${ruta.hora_inicio} - ${ruta.hora_fin}`,
  }));

  return (
    <MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <MDTypography variant="h5">Rutas y Despachos</MDTypography>
        <MDButton
          variant="gradient"
          color="info"
          startIcon={<Icon>add</Icon>}
          onClick={() => handleOpenDialog()}
        >
          Nueva Ruta
        </MDButton>
      </MDBox>

      {/* Estadísticas Rápidas */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={3}>
          <Card>
            <MDBox p={2} textAlign="center">
              <Icon fontSize="large" color="info">
                route
              </Icon>
              <MDTypography variant="h4" fontWeight="bold" mt={1}>
                {rutas.length}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Total de Rutas
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <MDBox p={2} textAlign="center">
              <Icon fontSize="large" color="warning">
                directions_car
              </Icon>
              <MDTypography variant="h4" fontWeight="bold" mt={1}>
                {rutas.filter((r) => r.estado === "en_curso").length}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                En Curso
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <MDBox p={2} textAlign="center">
              <Icon fontSize="large" color="success">
                check_circle
              </Icon>
              <MDTypography variant="h4" fontWeight="bold" mt={1}>
                {rutas.filter((r) => r.estado === "completada").length}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Completadas Hoy
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <MDBox p={2} textAlign="center">
              <Icon fontSize="large" color="primary">
                speed
              </Icon>
              <MDTypography variant="h4" fontWeight="bold" mt={1}>
                {rutas.reduce((sum, r) => sum + r.distancia, 0).toFixed(1)} km
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Distancia Total
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>
      </Grid>

      {/* Tabla de Rutas */}
      <Card>
        <DataTable
          table={{ columns, rows }}
          showTotalEntries={true}
          isSorted={true}
          noEndBorder
          entriesPerPage={false}
          canSearch
        />
      </Card>

      {/* Dialog para Crear/Editar Ruta */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editando ? "Editar Ruta" : "Nueva Ruta"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Código"
                name="codigo"
                value={rutaActual.codigo}
                onChange={handleInputChange}
                placeholder="RUT-001"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nombre de la Ruta"
                name="nombre"
                value={rutaActual.nombre}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Conductor"
                name="conductor_id"
                value={rutaActual.conductor_id}
                onChange={handleInputChange}
                required
              >
                {conductoresDisponibles.map((conductor) => (
                  <MenuItem key={conductor.id} value={conductor.id}>
                    {conductor.nombre} - Licencia: {conductor.licencia}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Vehículo"
                name="vehiculo_id"
                value={rutaActual.vehiculo_id}
                onChange={handleInputChange}
                required
              >
                {vehiculosDisponibles.map((vehiculo) => (
                  <MenuItem key={vehiculo.id} value={vehiculo.id}>
                    {vehiculo.placa} - {vehiculo.tipo} ({vehiculo.capacidad})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Zona de Distribución"
                name="zona_id"
                value={rutaActual.zona_id}
                onChange={handleInputChange}
                required
              >
                {zonasDisponibles.map((zona) => (
                  <MenuItem key={zona.id} value={zona.id}>
                    {zona.nombre}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Fecha"
                name="fecha_inicio"
                value={rutaActual.fecha_inicio}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="time"
                label="Hora de Inicio"
                name="hora_inicio"
                value={rutaActual.hora_inicio}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="time"
                label="Hora de Fin"
                name="hora_fin"
                value={rutaActual.hora_fin}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Tiempo Estimado"
                value={rutaActual.tiempo_estimado}
                InputProps={{ readOnly: true }}
                helperText="Calculado automáticamente"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Distancia (km)"
                name="distancia_km"
                value={rutaActual.distancia_km}
                onChange={handleInputChange}
                inputProps={{ min: 0, step: 0.1 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Número de Paradas"
                name="paradas"
                value={rutaActual.paradas}
                onChange={handleInputChange}
                inputProps={{ min: 1 }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Observaciones"
                name="observaciones"
                value={rutaActual.observaciones}
                onChange={handleInputChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleGuardar} variant="contained" color="info">
            {editando ? "Actualizar" : "Crear"}
          </Button>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}
